import React, { useState } from "react";
import {
  Box,
  Typography,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
} from "@mui/material";
import { LocationOn } from "@mui/icons-material";
import useClientDetails from "../../hooks/useClientDetails";
import JobLocationMap from "./JobLocationMap";

const haversine = (lat1, lng1, lat2, lng2) => {
  const toRad = (v) => (v * Math.PI) / 180;
  const R = 6371000;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
};

const formatDistance = (meters) => {
  if (meters == null) return "—";
  return meters >= 1000
    ? `${(meters / 1000).toFixed(2)} km`
    : `${Math.round(meters)} m`;
};

const getDistanceSeverity = (meters) => {
  if (meters == null)
    return { color: "#667085", bg: "#F2F4F7", border: "#EAECF0", label: "No Data" };
  if (meters <= 300)
    return { color: "#027A48", bg: "#ECFDF3", border: "#ABEFC6", label: "Verified" };
  if (meters <= 1000)
    return { color: "#B54708", bg: "#FFFAEB", border: "#FEDF89", label: "Nearby" };
  return { color: "#B42318", bg: "#FEF3F2", border: "#FECDCA", label: "Far" };
};

const LocationAuditCell = ({ job }) => {
  const { clientDetails } = useClientDetails(job.client_id || job.client);
  const [showMap, setShowMap] = useState(false);

  const emp = job.employee_details?.[0];

  const carer =
    emp?.start_lat && emp?.start_lng
      ? {
          lat: Number(emp.start_lat),
          lng: Number(emp.start_lng),
          name: `${emp.first_name || ""} ${emp.last_name || ""}`.trim(),
        }
      : null;

  const addr = clientDetails?.addresses?.[0];
  const client =
    addr?.lat && addr?.lng
      ? {
          lat: Number(addr.lat),
          lng: Number(addr.lng),
          name: job.client_name,
          address: [addr.address_1, addr.address_2, addr.town, addr.postcode]
            .filter(Boolean)
            .join(", "),
        }
      : null;

  const distance =
    carer && client
      ? haversine(carer.lat, carer.lng, client.lat, client.lng)
      : null;

  const severity = getDistanceSeverity(distance);

  if (!carer || !client) {
    return (
      <Tooltip title="No location data available" arrow>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            px: 1,
            py: 0.4,
            borderRadius: "6px",
            bgcolor: "#F2F4F7",
            border: "1px solid #EAECF0",
          }}
        >
          <LocationOn sx={{ fontSize: 13, color: "#98A2B3" }} />
          <Typography
            sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#667085" }}
          >
            Not visited
          </Typography>
        </Box>
      </Tooltip>
    );
  }

  return (
    <>
      <Tooltip title="Click to view map" arrow>
        <Box
          onClick={() => setShowMap(true)}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            px: 1,
            py: 0.4,
            borderRadius: "6px",
            bgcolor: severity.bg,
            border: `1px solid ${severity.border}`,
            color: severity.color,
            fontWeight: 700,
            fontSize: "0.7rem",
            cursor: "pointer",
            transition: "0.15s",
            "&:hover": { transform: "scale(1.03)" },
          }}
        >
          <LocationOn sx={{ fontSize: 13 }} />
          {formatDistance(distance)}
        </Box>
      </Tooltip>

      <Dialog
        open={showMap}
        onClose={() => setShowMap(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LocationOn sx={{ color: "#1570EF" }} />
            Carer Location Audit — {job.client_name}
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mb: 2,
              p: 1.5,
              bgcolor: "#F9FAFB",
              borderRadius: 1.5,
              border: "1px solid #EAECF0",
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="caption"
                sx={{ color: "#667085", fontWeight: 600 }}
              >
                CARER START
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {carer.name || "Carer"}
              </Typography>
              <Typography variant="caption" sx={{ color: "#667085" }}>
                {carer.lat.toFixed(5)}, {carer.lng.toFixed(5)}
              </Typography>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography
                variant="caption"
                sx={{ color: "#667085", fontWeight: 600 }}
              >
                CLIENT ADDRESS
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {client.name || "Service User"}
              </Typography>
              <Typography variant="caption" sx={{ color: "#667085" }}>
                {client.address || "—"}
              </Typography>
            </Box>

            <Box sx={{ textAlign: "right" }}>
              <Typography
                variant="caption"
                sx={{ color: "#667085", fontWeight: 600 }}
              >
                DISTANCE
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 800, color: severity.color }}
              >
                {formatDistance(distance)}
              </Typography>
              <Chip
                label={severity.label}
                size="small"
                sx={{
                  mt: 0.5,
                  height: 20,
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  bgcolor: severity.bg,
                  color: severity.color,
                  border: `1px solid ${severity.border}`,
                }}
              />
            </Box>
          </Box>

          <JobLocationMap carer={carer} client={client} height={420} />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setShowMap(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LocationAuditCell;